/**
 * Command union — spec §11, transcribed in full.
 *
 * This is the entire surface through which the UI may affect the simulation.
 * The UI reads state snapshots and issues these; it never mutates state. Every
 * new interaction gets a command here rather than a direct write, because the
 * moment that stops being true, replay, offline catch-up, and any future
 * server-side verification all stop being possible.
 */

import type { GoodId, BuildingId, DroneClass } from './data';
import type { DroneId, HexCoord, PlotCoord, TradeableId } from './types';

export type Command =
  // No mineral parameter: drones strip mine, so the hex IS the choice (E13).
  | { type: 'assignDrone'; droneId: DroneId; hex: HexCoord }
  | { type: 'queueBuild'; plots: PlotCoord[]; building: BuildingId }
  | { type: 'reorderQueue'; from: number; to: number }
  | { type: 'cancelQueued'; index: number }
  | { type: 'demolish'; plot: PlotCoord }
  | { type: 'buildUnit'; unit: 'drone' | 'mech'; class?: DroneClass }
  // Drop an order still waiting for the assembly bay. Free: nothing has been
  // spent until an order reaches the bay.
  | { type: 'cancelAssembly'; index: number }
  | { type: 'setSynthesis'; good: GoodId; enabled: boolean; priority: number; target?: number }
  // Phase 10. A completed building runs only when switched on.
  | { type: 'setBuildingEnabled'; plot: PlotCoord; enabled: boolean }
  // Phase 10. Colonists arrive when the player decides they will be content.
  | { type: 'requestColonists' }
  // Phase 11. Standing orders: sell above a reserve, or buy up to a target.
  | {
      type: 'setMarketOrder';
      element: TradeableId;
      mode: 'idle' | 'sell' | 'buy';
      threshold: number;
    }
  // Not in the spec's §11 list, but §13 item 3 specifies the toggle and it has
  // to reach the sim somehow.
  | { type: 'setDroneAutoReassign'; droneId: DroneId; autoReassign: boolean };

export type CommandType = Command['type'];

const KNOWN_COMMAND_TYPES: ReadonlySet<string> = new Set<CommandType>([
  'assignDrone',
  'queueBuild',
  'reorderQueue',
  'cancelQueued',
  'demolish',
  'buildUnit',
  'setSynthesis',
  'setDroneAutoReassign',
  'setBuildingEnabled',
  'requestColonists',
  'setMarketOrder',
  'cancelAssembly',
]);

/**
 * Reject unknown commands loudly rather than ignoring them.
 *
 * A dropped command is the worst class of bug in a system like this: the sim
 * stays internally consistent and the hash stays stable, so nothing fails —
 * the player's instruction just quietly evaporates.
 */
export function assertKnownCommand(command: Command): void {
  if (!KNOWN_COMMAND_TYPES.has(command.type)) {
    throw new Error(
      `Unknown command type ${JSON.stringify((command as { type: string }).type)}. ` +
        `Commands must be declared in src/sim/commands.ts (spec §11).`,
    );
  }
}
